/** Skupiny týmu — sekce na stránce Náš tým (hierarchicky shora dolů). */
export const TEAM_GROUPS = [
  { key: "vedeni", title: "Vedení" },
  { key: "lektori", title: "Lektoři a konzultanti" },
  { key: "zazemi", title: "Zázemí a organizace" },
] as const;

export type TeamGroupKey = (typeof TEAM_GROUPS)[number]["key"];

export interface TeamMember {
  slug: string;
  name: string;
  /** Do které sekce stránky člen patří. */
  group: TeamGroupKey;
  /** Krátká tagline-style role, jednořádková. */
  role: string;
  /** Foto (volitelné). Když chybí, vykreslí se fallback-avatar z iniciál. */
  photoSrc?: string;
  photoAlt?: string;
  /** 3–4 oblasti specializace — zobrazují se jako tagy. */
  specialties: readonly string[];
  /** První věta = hook nad highlights v dialogu. */
  hook: string;
  /** Bullety s konkrétními proof points / čísly. */
  highlights: readonly string[];
  /** Delší medailonek — „povídání“ o člověku (volitelné). Renderuje se jako odstavec. */
  bio?: string;
  /** Osobní citát (volitelné) — renderuje se v uvozovkách jako blockquote. */
  quote?: string;
  /** Externí social linky (volitelné). */
  linkedinUrl?: string;
  instagramUrl?: string;
  facebookUrl?: string;
  tiktokUrl?: string;
  /** Osobní / firemní web (volitelné). */
  webUrl?: string;
  /** Krátký štítek dočasné nedostupnosti (např. „Na mateřské“). Fotka se zašedne. */
  onLeave?: string;
}

export const TEAM: readonly TeamMember[] = [
  {
    slug: "ondrej-krelina",
    group: "vedeni",
    name: "Ondřej Křelina",
    role: "CEO a majitel HCA Moravia",
    photoSrc: "/hca-team/ondra.jpg",
    photoAlt: "Ondřej Křelina — CEO HCA Moravia",
    specialties: ["Obchod", "Řízení firmy", "Komunikace", "Leadership"],
    hook: "13 let pomáhá českým firmám zdravěji fungovat a přirozeně expandovat.",
    highlights: [
      "Proškolil přes 5 500 lidí ve více než 600 firmách",
      "Nastavení obchodu, komunikace a řízení výkonu",
      "Budování odpovědností, procesů a funkční organizace",
      "Připravuje firmy na generační obměnu, prodej nebo expanzi",
    ],
    bio: "Přes 13 let pomáhám majitelům, CEO a jejich týmům stavět firmy, které rostou zdravěji, výkonněji a s větší kontrolou. Podílel jsem se na výsledcích, které se v součtu pohybují v miliardových hodnotách — včetně generačních předání, příprav na prodej, akvizic i další expanze. Jedenáct let jsem úzce spolupracoval s Martinem Koleničkou, zakladatelem HCA Czechia; dnes vedu HCA Moravia s cílem navázat na hodnoty, které do ní vložil. Jsem také otec dvou dětí, což mi každý den připomíná, že skutečný leadership začíná odpovědností, příkladem a schopností být přítomný tam, kde na tom záleží.",
    quote:
      "Věřím, že firma může být silná, zisková a zároveň lidská. A že skutečný růst začíná ve chvíli, kdy majitel získá zpět kontrolu a odvahu udělat věci správně.",
    linkedinUrl: "https://www.linkedin.com/in/ondrakrelina/",
    instagramUrl: "https://www.instagram.com/ondrej_krelina/",
    facebookUrl: "https://www.facebook.com/ondrej.krelina/",
    tiktokUrl: "https://www.tiktok.com/@ok_krelina",
  },
  {
    slug: "ladislav-pavlik",
    group: "lektori",
    name: "Ladislav Pavlík",
    role: "Majitel a prezident HCA Slovakia",
    photoSrc: "/hca-team/ladislav-pavlik.jpg",
    photoAlt: "Ladislav Pavlík — prezident HCA Slovakia",
    specialties: ["Strategie", "Leadership", "Řízení firem", "Předávání firem"],
    hook: "Prezident Školy manažmentu L. R. Hubbarda — do praxe zavedl přes 200 firemních struktur v ČR i na Slovensku.",
    highlights: [
      "20 let praktických zkušeností v managementu",
      "Školu manažmentu založil v roce 2001 a vede ji jako nejúspěšnější v celé mezinárodní síti",
      "Jeho semináře o managementu, komunikaci a organizaci firmy absolvovalo přes 5 500 manažerů",
      "V ČR i SR zavedl do praxe přes 200 firemních struktur",
    ],
    bio: "V letech 1991–2000 byl spolumajitelem úspěšné firmy se 120 zaměstnanci a pěti pobočkami na Slovensku. Vzdělávání se věnuje od roku 1997 — jeho přednášky o managementu, mezilidských vztazích, komunikaci, řešení stresu a organizování firmy absolvovalo přes 5 500 manažerů. Připravuje na vydání knihu o praktickém managementu.",
  },
  {
    slug: "heimo-bucerius",
    group: "lektori",
    name: "Heimo Bucerius",
    role: "Externí lektor — Finanční prosperita",
    photoSrc: "/hca-team/heimo-bucerius.jpg",
    photoAlt: "Heimo Bucerius — externí lektor",
    specialties: ["Finanční plánování", "Strategie", "Marketing", "Firemní struktura"],
    hook: "Trénovaný finanční a manažerský konzultant s více než dvacetiletou praxí — osobně odškolil přes 6 000 účastníků.",
    highlights: [
      "20+ let přednáší o finančním a strategickém plánování, marketingu a firemní struktuře",
      "Osobně odškolil více než 6 000 účastníků z různých oborů",
      "Zakladatel poradenské firmy KONZEPT plus GRUNDBESITZ GmbH (nemovitosti, investice, pojištění)",
      "Pomohl klientům prodat, získat či investovat do více než 11 000 nemovitostí a strategických investic",
    ],
    bio: "Je trénovaným finančním a manažerským konzultantem Hubbardovy technologie managementu. S více než dvacetiletou zkušeností přednáší semináře o finančním a strategickém plánování, marketingu a firemní struktuře. Jeho firma KONZEPT plus GRUNDBESITZ se věnuje poradenství v oblasti prodeje a stavby nemovitostí, strategických investic, finančních produktů a pojišťovnictví. Žije v Německu.",
  },
  {
    slug: "petr-jansky",
    group: "lektori",
    name: "Petr Jánský",
    role: "Lektor — Jak sepsat směrnice a klobouky",
    photoSrc: "/hca-team/petr-jansky.jpg",
    photoAlt: "Petr Jánský — lektor",
    specialties: ["Personalistika", "Pracovní popisy", "Firemní struktury", "Vzdělávání"],
    hook: "Konzultant a trenér, který pomáhá podnikatelům expandovat přes jasné pracovní popisy a firemní know-how.",
    highlights: [
      "Práci s lidmi se aktivně věnuje od roku 2005",
      "Část profesní kariéry strávil v personalistice ve Velké Británii",
      "V HCA Brno pomohl vytvořit přibližně 100 pracovních popisů a řadu firemních struktur",
      "Vytrénovaný přednášející v oblasti lidské mysli",
    ],
    bio: "Pomáhá podnikatelům a jejich spolupracovníkům naplňovat vize a expandovat. Specializuje se na efektivní sepsání a používání pracovních popisů a firemního know-how; práci s lidmi se aktivně věnuje od roku 2005 a část profesní kariéry strávil v personalistice ve Velké Británii. Je ženatý a jeho žena se věnuje osobnímu rozvoji.",
  },
  {
    slug: "miroslav-ostry",
    group: "lektori",
    name: "Ing. Miroslav Ostrý",
    role: "Lektor a mentor — řízení výrobních firem",
    photoSrc: "/hca-team/miroslav-ostry.jpg",
    photoAlt: "Ing. Miroslav Ostrý — lektor a mentor",
    specialties: ["Řízení výroby", "Procesy", "Restrukturalizace", "Leadership"],
    hook: "Zkušený manažer výrobních firem (HESTEGO, KSK Kuřim), který dnes předává praxi majitelům, ředitelům a týmům.",
    highlights: [
      "Přes 40 let praxe — od projektování a stavebnictví po vrcholové řízení (VUT Brno, 1984)",
      "V letech 2006–2025 postupně vedl 4 společnosti, mj. HESTEGO a KSK Kuřim",
      "Reorganizací výroby dokázal navýšit produkci na m² až čtyřnásobně",
      "Leadership staví na slušnosti, výběru správných lidí a firemní kultuře",
    ],
    bio: "Od roku 1984, kdy absolvoval Ekonomiku a řízení na VUT v Brně, prošel projektováním, stavebnictvím i vrcholovým řízením. V letech 2006–2025 vedl postupně čtyři společnosti, mimo jiné HESTEGO a KSK Kuřim. Dnes v „manažerském důchodu“ předává praxi majitelům, ředitelům a týmům — a energii dobíjí na zahradě a domečku na Vysočině.",
  },
  {
    slug: "jan-slapal",
    group: "lektori",
    name: "Jan Šlapal",
    role: "Lektor — finanční řízení a škálování firem",
    photoSrc: "/hca-team/jan-slapal.jpg",
    photoAlt: "Jan Šlapal — lektor",
    specialties: ["Škálování firmy", "Finance", "Restrukturalizace", "Leadership"],
    hook: "Ukazuje majitelům a CEO, jak přerůst vlastní operativu a postavit firmu, která roste bez závislosti na jednom člověku.",
    highlights: [
      "Škálování firmy z osobního řízení na systémové",
      "Přechod majitele z operativy do strategické role",
      "Finance, controlling, restrukturalizace a řízení výkonu",
      "Rozvoj manažerů přímo ve firemní realitě, ne v teorii",
    ],
    bio: "Pomáhá manažerům změnit způsob uvažování z malé firmy na velkou — od hašení problémů k řízení přes systém, čísla, odpovědnost a leadership. Vychází z praxe vrcholového řízení, financí, restrukturalizace a škálování firem.",
    linkedinUrl: "https://www.linkedin.com/in/jan-slapal-a453915/",
  },
  {
    slug: "jaroslav-suster",
    group: "lektori",
    name: "Jaroslav Šuster, MBA",
    role: "Lektor — obchod, řízení a týmy",
    photoSrc: "/hca-team/jaroslav-suster.jpg",
    photoAlt: "Jaroslav Šuster — lektor",
    specialties: ["Obchodní řízení", "Business strategie", "Projektové řízení", "Procesy"],
    hook: "Propojuje čísla, lidi a systém tak, aby firma rostla zdravě, výkonně a dlouhodobě.",
    highlights: [
      "Obchodní řízení a růst tržeb",
      "Business strategie převedená do praxe",
      "Projektové řízení a exekuce",
      "Optimalizace procesů a vedení týmů",
    ],
    quote:
      "Věřím v praktická řešení, která nejsou jen na papíře, ale dají se rovnou použít ve firmě. Mojí silnou stránkou je propojit čísla, lidi a systém tak, aby firma rostla zdravě, výkonně a dlouhodobě.",
  },
  {
    slug: "jakub-kanovsky",
    group: "lektori",
    name: "Jakub Kaňovský",
    role: "Lektor — finanční gramotnost",
    photoSrc: "/hca-team/jakub-kanovsky.jpg",
    photoAlt: "Jakub Kaňovský — lektor",
    specialties: ["Finanční gramotnost", "Hypotéky", "Investice", "Finanční plán"],
    hook: "Pomáhá lidem mít finance pod kontrolou — od hypoték přes investice až po dlouhodobý finanční plán.",
    highlights: [
      "Přes 500 mil. Kč ve správě a 100+ investičních nemovitostí pořízených klienty",
      "98 % doporučení a 32 ověřených hodnocení",
      "Vede workshopy finanční gramotnosti — i formou vlastní deskové hry",
      "Ze sportu si nese disciplínu: 10 let v mládeži 1. FC Slovácko",
    ],
    quote:
      "Pomáhám lidem mít finance pod kontrolou — od hypoték přes investice až po dlouhodobý finanční plán. Vedu workshopy finanční gramotnosti a používám i deskovou hru, aby finance dávaly lidem konečně smysl.",
    instagramUrl: "https://www.instagram.com/jakub_kanovsky/",
    webUrl: "https://jakubkanovsky.lovable.app",
  },
  {
    slug: "ivana-sekerakova",
    group: "lektori",
    name: "Ivana Sekeráková",
    role: "Lektorka HCA SK — Efektivní marketing",
    photoSrc: "/hca-team/ivana-sekerakova.jpg",
    photoAlt: "Ivana Sekeráková — lektorka HCA SK",
    specialties: ["Marketing", "Průzkumy", "Strategie", "Analýza zákazníků"],
    hook: "Dělá marketing, který nestojí na pocitu, ale na průzkumech, strategii a měřitelných výsledcích.",
    highlights: [
      "Dlouhodobě vede seminář Efektivní marketing",
      "Marketing postavený na průzkumech a tvorbě poptávky",
      "Strategie, marketingový plán a analýza konkurence i zákazníků",
      "Měření efektivity marketingu",
    ],
    quote:
      "Pomáhám firmám dělat marketing, který nestojí na pocitu, ale na průzkumech, strategii a měřitelných výsledcích. Učím firmy, jak pochopit zákazníka, vytvořit poptávku a nastavit marketing tak, aby skutečně přiváděl nové zákazníky.",
  },
  {
    slug: "hana-malinic",
    group: "vedeni",
    name: "Hana Malinič",
    role: "Marketingová ředitelka · koučka, mentorka a lektorka",
    photoSrc: "/hca-team/hana-malinic.jpg",
    photoAlt: "Hana Malinič — koučka, mentorka a lektorka",
    specialties: ["Leadership", "Marketing", "Obchod", "Organizační systémy"],
    hook: "Firma vyrastie maximálne na úroveň človeka, ktorý ju vedie.",
    highlights: [
      "13 rokov praxe v marketingu a biznise",
      "Pomáha majiteľom firiem a manažérom s leadershipom",
      "Práca na obchodných zručnostiach a organizačných systémoch",
      "Buduje firmy od základu, nie od povrchu",
    ],
    bio: "Strávila 13 rokov v marketingu a biznise, kým pochopila, že systémy a procesy fungujú len vtedy, keď lídri za nimi vedia, kto sú a kam idú. Dnes pomáha majiteľom firiem a manažérom pracovať na leadershipu, obchodných zručnostiach a organizačných systémoch — od základu, nie od povrchu.",
    linkedinUrl: "https://www.linkedin.com/in/hana-malinič/",
    instagramUrl: "https://www.instagram.com/hana.malinic",
    webUrl: "https://hanamalinic.com/",
  },
  {
    slug: "vojtech-barton",
    group: "lektori",
    name: "Vojtěch Bartoň",
    role: "Odborník na marketing a budování značek",
    photoSrc: "/hca-team/vojtech-barton.jpg",
    photoAlt: "Vojtěch Bartoň — marketing a budování značek",
    specialties: ["Marketing", "Osobní branding", "Sociální sítě"],
    hook: "Buduje osobní a firemní značky nejen na sociálních sítích.",
    highlights: [
      "Zakladatel a CEO agentury BoomBrand",
      "Přes 100 milionů zhlédnutí pro klienty",
      "Spolupracuje s firmami jako T-Mobile ČR",
      "Vede podcast TalkToWin",
    ],
    bio: "Podnikatel z Brna, zakladatel a CEO agentury BoomBrand, která se zaměřuje na budování dlouhodobých osobních a firemních značek. Pro klienty získal přes 100 milionů zhlédnutí, spolupracuje s firmami jako T-Mobile ČR a vede podcast TalkToWin.",
    quote: "Věřím, že podnikání je hra — a chci, aby ji hráli co nejlépe.",
    linkedinUrl: "https://www.linkedin.com/in/vojtabarton/",
    instagramUrl: "https://www.instagram.com/vojta__barton/",
    webUrl: "https://boom-brand.cz/",
  },
  {
    slug: "anna-smigova",
    group: "lektori",
    name: "Anna Šmigová",
    role: "Konzultantka, poradkyně pro vzdělávání a koučka",
    photoSrc: "/hca-team/anna.jpg",
    photoAlt: "Anna Šmigová — konzultantka a koučka",
    specialties: ["Obchod", "Osobnostní rozvoj", "Vedení lidí"],
    hook: "Koučka a lektorka s 8+ lety praxe v B2B obchodu.",
    highlights: [
      "8+ let praktické zkušenosti v B2B obchodu",
      "5+ let v osobnostním rozvoji a vedení lidí",
      "Bohaté zkušenosti s organizováním rozvojových akcí",
    ],
    quote:
      "Pomáhat lidem zlepšovat jejich život i podnikání mi dává hluboký smysl. Věřím, že je důležité na sobě stále pracovat, růst a nenechat se odradit. Miluju tanec, sport, cestování, kulturu a nové výzvy. Když něco chci, jdu si za tím.",
  },
  {
    slug: "martin-pospisil",
    group: "lektori",
    name: "Martin Pospíšil",
    role: "Obchodník a konzultant — obchod a výroba",
    photoSrc: "/hca-team/martin-pospisil.jpg",
    photoAlt: "Martin Pospíšil — obchodník a konzultant",
    specialties: ["Obchod", "Výroba", "Praktické řízení", "Kreativní řešení"],
    hook: "Přes 25 let propojuje výrobu, obchod a hledání řešení tam, kde jiní končí větou „to nejde“.",
    highlights: [
      "25+ let zkušeností z výroby a tisku (zakladatel LLQ Print)",
      "Zkušenosti z ČR, USA i Kanady",
      "Prodej, školení a zavádění produktů do praxe",
      "3 roky konzultantem a obchodníkem pro klienty HCA",
    ],
    bio: "Zakladatel LLQ Print. Vyvíjel, vyráběl, prodával a školil práci s přístroji L.I.F.E. pro český trh, Evropu, USA i Kanadu — díky tomu rozumí nejen výrobě, ale i tomu, jak produkt dostat k zákazníkovi a naučit lidi s ním skutečně pracovat. Poslední 3 roky spolupracuje s HCA, kde pomáhá firmám jako obchodník a konzultant v oblasti obchodu, výroby a praktického řízení.",
    linkedinUrl: "https://www.linkedin.com/in/martin-pospisil-mp/",
  },
  {
    slug: "michaela-svobodova",
    group: "zazemi",
    name: "Michaela Svobodová",
    role: "Office manažerka",
    photoSrc: "/hca-team/michaela-svobodova.jpg",
    photoAlt: "Michaela Svobodová — Office Manager HCA Moravia",
    specialties: ["Office management", "Provoz", "Organizace seminářů"],
    hook: "Spojka mezi klienty, dodavateli a interním týmem — Ondrova pravá ruka.",
    highlights: [
      "10+ let praxe ve facility managementu",
      "3 roky na pozici Office Manager",
      "Stará se o organizaci seminářů, workshopů i každodenního provozu",
      "Propojuje administrativní, provozní a organizační agendu",
    ],
    quote:
      "Mám ráda lidi, komunikaci a dobře fungující a smysluplnou spolupráci. Vytvářím prostředí, které funguje a ve kterém se všichni cítí dobře. Ve volném čase ráda cestuji, chodím po horách, věnuji se focení a kresbě a inspiraci hledám i v gastronomii, zejména v asijské kuchyni.",
  },
  {
    slug: "adela-horvathova",
    group: "zazemi",
    name: "Adela Horváthová",
    role: "Office manažerka",
    photoSrc: "/hca-team/adela-horvathova.jpg",
    photoAlt: "Adela Horváthová — office manažerka",
    specialties: ["Office management", "Administrativa", "Organizace seminářů"],
    hook: "Ondrova pravá ruka a spojka mezi týmem a klienty — s důrazem na systém a pořádek v zázemí firmy.",
    highlights: [
      "Komplexní administrativní podpora týmu",
      "Zajišťuje plynulý průběh seminářů a workshopů",
      "Studuje Podnikovou ekonomiku a management na Masarykově univerzitě",
      "Komunikace s klienty a hladký chod kanceláře",
    ],
    bio: "Studium Podnikové ekonomiky a managementu na Masarykově univerzitě jí dává skvělou příležitost vidět, jak byznys a funkční systémy fungují přímo v praxi. Stará se o komplexní administrativní podporu týmu, plynulý průběh seminářů a workshopů a komunikaci s klienty.",
    quote:
      "Mám ráda, když mají věci jasný systém a ve všem vládne pořádek — a snažím se to přenášet i do chodu naší kanceláře. Mojí největší vášní je cestování a objevování nových zemí, ráda si čistím hlavu při sportu a jako správný gastro nadšenec nikdy neodmítnu dobré a zajímavé jídlo.",
  },
];
